import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injector } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { CommandController, FormMetadaDataDom } from '@farris/devkit';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { concatMap, reduce, tap } from 'rxjs/operators';
import { CommandControllerBuilder } from '../../commands/command-controller-builder';
import { FORM_METADATA_RELATIVE_PATH_TOKEN } from '../token';

export class MetadataService {

  private commandControllerBuilder: CommandControllerBuilder;

  constructor(private httpClient: HttpClient, private injector: Injector) {
    this.commandControllerBuilder = new CommandControllerBuilder(httpClient);
  }

  public getMetadatas(route: ActivatedRoute, defaultMetadataContent: any)
    : Observable<Map<string, any>> {
    const metadatas = new Map<string, any>();
    return this.getFormMetadata(route, defaultMetadataContent)
      .pipe(
        concatMap((result: Map<string, any>) => {
          return this.getStateMachineMetadataContent(result);
        }),
        concatMap((result: Map<string, any>) => {
          return this.getCommandControllers(result);
        })
      );
  }

  private getFormMetadata(route: ActivatedRoute, defaultMetadataContent: any): Observable<Map<string, any>> {
    const metadatas = new Map<string, any>();
    return route.queryParams.pipe(
      concatMap((params: Params) => {
        if (params.url) {
          const metadataPath: string = params.url;
          return this.loadMetadatByMetadataPath(metadataPath).pipe(
            reduce((acc: any, formMetadataContent: FormMetadaDataDom) => {
              acc.set('formMetadataContent', formMetadataContent);
              return acc;
            }, metadatas)
          );
        }
        if (params.isRtc) {
          const isRootMetadata = params.isRootMetadata;
          const baseMetadataId = params.metadataId;
          const firstDimension = params.dim1;
          const secondDimension = params.dim2;
          return this.loadRuntimeCustomizeMetadata(baseMetadataId, firstDimension, secondDimension, isRootMetadata).pipe(
            reduce((acc: any, formMetadataContent: FormMetadaDataDom) => {
              acc.set('formMetadataContent', formMetadataContent);
              return acc;
            }, metadatas)
          );
        }
        return of(defaultMetadataContent).pipe(
          reduce((acc: any, formMetadataContent: FormMetadaDataDom) => {
            acc.set('formMetadataContent', formMetadataContent);
            return acc;
          }, metadatas)
        );
      })
    );
  }

  public getRelatedMetadatas(): Observable<Map<string, any>> {
    const metadatas = new Map<string, any>();
    return this.getFormMetadataContent(metadatas).pipe(
      concatMap((result: Map<string, any>) => {
        return this.getStateMachineMetadataContent(result);
      }),
      concatMap((result: Map<string, any>) => {
        return this.getCommandControllers(result);
      })
    );
  }

  public getRelatedMetadatasByFormMetadata(metadata: any) {
    return this.getRelatedMetadatasByFormMetadataAsync(of(metadata));
    // const metadatas = new Map<string, any>();
    // metadatas.set('formMetadataContent', metadata);
    // return of(metadatas).pipe(
    //   concatMap((result: Map<string, any>) => {
    //     return this.getStateMachineMetadataContent(result);
    //   }),
    //   concatMap((result: Map<string, any>) => {
    //     return this.getCommandControllers(result);
    //   })
    // );
  }

  public getRelatedMetadatasByFormMetadataAsync($metadata: Observable<any>) {
    const metadatas = new Map<string, any>();
    return $metadata.pipe(
      tap((formMetadataContent: any) => {
        metadatas.set('formMetadataContent', formMetadataContent);
      }),
      concatMap((result: Map<string, any>) => {
        return this.getStateMachineMetadataContent(result);
      }),
      concatMap((result: Map<string, any>) => {
        return this.getCommandControllers(result);
      })
    );
  }

  private getFormMetadataContent(metadatas: Map<string, any>): Observable<Map<string, any>> {
    const metadataPath = this.getQueryVariable('url');
    const uri = metadataPath.replace(/\\/g, '/');
    const fileName = uri.substring(uri.lastIndexOf('/') + 1);
    const filePath = uri.substring(1, uri.lastIndexOf('/'));
    return this.loadMetadata(fileName, filePath).pipe(
      concatMap((data: any) => {
        let formMetaDataContent = JSON.parse(data.content).Contents as FormMetadaDataDom;
        if (typeof (formMetaDataContent) === 'string') {
          formMetaDataContent = JSON.parse(formMetaDataContent);
        }
        return of(formMetaDataContent);
      }),
      reduce((acc: any, formMetadataContent: FormMetadaDataDom) => {
        acc.set('formMetadataContent', formMetadataContent);
        return acc;
      }, metadatas)
    );
  }

  private getStateMachineMetadataContent(metadatas: Map<string, any>): Observable<Map<string, any>> {
    const formMetadataContent = metadatas.get('formMetadataContent') as FormMetadaDataDom;
    const formModule = formMetadataContent.module;
    if (formModule.stateMachines && formModule.stateMachines.length) {
      const metadataPath = this.injector.get(FORM_METADATA_RELATIVE_PATH_TOKEN, null) || this.getQueryVariable('url');
      const uri = metadataPath.replace(/\\/g, '/');
      const relativePath = uri.substring(1, uri.lastIndexOf('/'));
      const stataMachineId = formModule.stateMachines[0].uri;
      return this.loadMetadataByReferencePath(relativePath, stataMachineId)
        .pipe(
          concatMap((data: any) => {
            let stateMachineMetadataContent = JSON.parse(data.content);
            if (typeof (stateMachineMetadataContent) === 'string') {
              stateMachineMetadataContent = JSON.parse(stateMachineMetadataContent);
            }
            return of(stateMachineMetadataContent);
          }),
          reduce((acc: any, stateMachineContent: any) => {
            acc.set('stateMachineMetadataContent', stateMachineContent);
            return acc;
          }, metadatas)
        );
    } else {
      return of(metadatas);
    }
  }

  private getCommandControllers(metadatas: Map<string, any>): Observable<Map<string, any>> {
    const formMetadataContent = metadatas.get('formMetadataContent') as FormMetadaDataDom;
    const cmds = formMetadataContent.module.webcmds;
    const controllerIds = cmds.map(command => command.name);
    if (controllerIds && controllerIds.length) {
      return this.commandControllerBuilder.createCommandControllers(controllerIds)
        .pipe(
          concatMap((controllers: { [id: string]: CommandController }) => {
            metadatas.set('commandControllers', controllers);
            return of(metadatas);
          })
        );
    } else {
      return of(metadatas);
    }
  }

  private getQueryVariable(variable) {
    const queryString = window.location.search.substring(window.location.search.indexOf('?') + 1);
    const queryParamTokens = queryString.split('&');
    for (const token of queryParamTokens) {
      const paramKeyValuePairs = token.split('=');
      if (paramKeyValuePairs[0] === variable) {
        return paramKeyValuePairs[1];
      }
    }
  }

  private loadMetadatByMetadataPath(metadataPath: string): Observable<any> {
    const uri = metadataPath.replace(/\\/g, '/');
    const fileName = uri.substring(uri.lastIndexOf('/') + 1);
    const filePath = uri.substring(1, uri.lastIndexOf('/'));
    return this.loadMetadata(fileName, filePath).pipe(
      concatMap((data: any) => {
        let formMetaDataContent = JSON.parse(data.content).Contents as FormMetadaDataDom;
        if (typeof (formMetaDataContent) === 'string') {
          formMetaDataContent = JSON.parse(formMetaDataContent);
        }
        return of(formMetaDataContent);
      })
    );
  }

  private loadMetadata(fullName: string, path: string): Observable<any> {
    const sessionId = localStorage.getItem('sessionId');
    const headers = new HttpHeaders();
    headers.set('SessionId', sessionId);
    const SERVER_IP = '';
    const METADATA_BASE_PATH = '/api/dev/main/v1.0/metadatas';
    const metadataFullPath = path.replace(/\\/g, '/') + '/' + fullName;
    const encodedMetadataFullPath = encodeURIComponent(metadataFullPath);
    const url = SERVER_IP + METADATA_BASE_PATH + '/load?metadataFullPath=' + encodedMetadataFullPath;
    return this.httpClient.get(url, { headers });
  }

  private loadMetadataByReferencePath(path, metadataId) {
    const sessionId = localStorage.getItem('sessionId');
    const headers = new HttpHeaders();
    headers.set('SessionId', sessionId);
    const SERVER_IP = '';
    const METADATA_BASE_PATH = '/api/dev/main/v1.0/metadatas';
    const url = SERVER_IP + METADATA_BASE_PATH + '/relied?metadataPath=' + path + '&metadataID=' + metadataId;
    return this.httpClient.get(url, { headers });
  }

  private loadRuntimeCustomizeMetadata(baseMetadataId: string, firstDimension: string, secondDimension: string, isRootMetadata: boolean) {
    const sessionId = localStorage.getItem('sessionId');
    const headers = new HttpHeaders();
    headers.set('SessionId', sessionId);
    const SERVER_IP = '';
    // http://localhost:5300/api/runtime/bcc/v1.0/template/renderContext?metadataId=a&dim1=public&dim2=public&isRootMetadata=true
    const METADATA_BASE_PATH = '/api/runtime/bcc/v1.0/template/renderContext';
    // const url = `./${firstDimension}/${secondDimension}/metadata.json`;
    const url = SERVER_IP + METADATA_BASE_PATH + '/relied?metadataId=' + baseMetadataId
      + '&dim1=' + firstDimension + '&dim2=' + secondDimension + '&isRootMetadata=' + isRootMetadata;
    return this.httpClient.get(url, { headers });
  }
}
